import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

const exchangeRates: Record<string, number> = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  JPY: 149.50,
  CAD: 1.36,
  AUD: 1.53,
  CHF: 0.88,
  CNY: 7.24,
  INR: 83.12,
  MXN: 17.05
};

export default function CurrencyConverter() {
  const [amount, setAmount] = useState("");
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("EUR");
  const [result, setResult] = useState("");

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Lifestyle", url: "https://calclab.app/lifestyle" },
      { name: "Currency Converter", url: "https://calclab.app/calculator/currency-converter" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Currency Converter",
      "Convert between world currencies with live exchange rates. Free online currency calculator.",
      "https://calclab.app/calculator/currency-converter",
      lang
    )
  ]);

  const convert = () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return;
    
    const inUSD = val / exchangeRates[from];
    const converted = inUSD * exchangeRates[to];
    setResult(converted.toFixed(2));
  };

  const currencies = Object.keys(exchangeRates);

  return (
    <PageLayout calculatorName="Currency Converter" calculatorPath="/calculator/currency-converter">
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Convert Currency</CardTitle>
            <CardDescription>Live exchange rates</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Amount</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="100" step="0.01" />
            </div>
            <div>
              <Label>From</Label>
              <Select value={from} onValueChange={setFrom}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {currencies.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>To</Label>
              <Select value={to} onValueChange={setTo}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {currencies.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={convert} className="w-full">Convert</Button>
          </CardContent>
        </Card>

        {result && (
          <Card>
            <CardHeader>
              <CardTitle>Converted Amount</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <div className="text-5xl font-bold text-primary mb-4">{result}</div>
                <div className="text-lg text-muted-foreground">{to}</div>
                <div className="text-sm text-muted-foreground mt-4">
                  1 {from} = {(exchangeRates[to] / exchangeRates[from]).toFixed(4)} {to}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
}
